import React, { useEffect, useState, useCallback } from "react";
import "./Dashboard.css";
import { supabase } from "../SupabaseClient.tsx";
import {
  PiClock,
  PiCaretDown,
  PiCaretUp,
  PiCaretUpFill,
  PiPlus,
  PiUploadSimple,
  PiWarning,
} from "react-icons/pi";
import FirstReceipt from "../components/FirstReceipt.tsx";
import FourTileMetrics from "../components/FourTileMetrics.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import { countDraftReceipts, viewDraftReceipts } from "../utils/draftReceipts.ts";
import { countFilesInStorage, saveFileToStorage } from "../utils/uploadStorage.ts";
import { daysUntil, getCheckoutUrl } from "../utils/checkout.ts";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import ActionButton from "../components/ActionButton.tsx";
import { useNavigate } from "react-router-dom";

interface OrderData {
  id: string;
  purchase_date: string;
  reimbursable_amount: number;
}

interface ChartDataPoint {
  date: string;
  total: number;
  formattedDate: string;
  isActualDataPoint: boolean; // Add this flag to mark real data points
}

interface SavingsChartData {
  purchase_date: string;
  amount: number;
}

interface DashboardProps {
  // When a trial without a card ends, in Stripe's seconds since the epoch
  trialEndsAt?: number | null;
  userEmail?: string;
}

const Dashboard = ({ trialEndsAt = null, userEmail }: DashboardProps) => {
  const navigate = useNavigate();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [dailySavings, setDailySavings] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [exampleReceipt, setExampleReceipt] = useState<OrderData | null>(null);
  const [interestRate, setInterestRate] = useState<number>(8);
  const [twentyYearGrowth, setTwentyYearGrowth] = useState<number>(0);
  const [totalReceipts, setTotalReceipts] = useState<number>(0);
  const [availableFunds, setAvailableFunds] = useState<number>(0);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [chartLoading, setChartLoading] = useState<boolean>(true);
  const [draftCount, setDraftCount] = useState<number>(0);
  const [pendingUploadCount, setPendingUploadCount] = useState<number>(0);
  const [hasNoReceipts, setHasNoReceipts] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch receipts that still need review: email uploads awaiting
  // confirmation and uploads not yet added to the library
  useEffect(() => {
    countDraftReceipts().then(setDraftCount);
    countFilesInStorage()
      .then(setPendingUploadCount)
      .catch((error) => console.error("Error counting pending uploads:", error));
  }, []);

  // Calculate total compound growth for a collection of orders up to a specific date
  const calculateCompoundGrowth = (
    orders: OrderData[] | null,
    targetDate: Date,
    rate: number
  ): number => {
    if (!orders || orders.length === 0) return 0;

    let totalSavings = 0;

    orders.forEach((order: OrderData) => {
      const purchaseDate = new Date(order.purchase_date);
      const yearDiff =
        (targetDate.getTime() - purchaseDate.getTime()) /
        (1000 * 60 * 60 * 24 * 365);

      // Calculate compound growth: P(1+r)^t
      const growth =
        order.reimbursable_amount * Math.pow(1 + rate / 100, yearDiff);
      const savings = growth - order.reimbursable_amount;

      totalSavings += savings;
    });

    return totalSavings;
  };

  // Function to prepare running total data for the chart
  const prepareChartData = useCallback((savingsData: SavingsChartData[]) => {
    if (!savingsData.length) return;

    // Helper function to parse date string to date object, preserving the exact date
    const parseDate = (dateString: string): Date => {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day);
    };

    // Helper function to generate all dates between two dates
    const getDatesInRange = (startDate: Date, endDate: Date): Date[] => {
      const dates: Date[] = [];
      const currentDate = new Date(startDate);

      // Set hours to ensure consistent comparison
      currentDate.setHours(0, 0, 0, 0);
      const endDateNormalized = new Date(endDate);
      endDateNormalized.setHours(0, 0, 0, 0);

      // Add each date until we reach the end date
      while (currentDate <= endDateNormalized) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    };

    // Helper function to format date to ISO string but only keep YYYY-MM-DD part
    const formatDateISO = (date: Date): string => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;
    };

    // Helper function to format date for display
    const formatDateDisplay = (date: Date): string => {
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    };

    // Get earliest and latest dates
    const earliestDateStr = savingsData[0].purchase_date;
    const earliestDate = parseDate(earliestDateStr);
    const today = new Date();

    // Get all dates between earliest date and today
    const allDates = getDatesInRange(earliestDate, today);

    // Create a map of existing data points
    const savingsMap = new Map<string, number>();
    savingsData.forEach((item) => {
      savingsMap.set(item.purchase_date, item.amount);
    });

    // Create consistent data points with running total
    let runningTotal = 0;
    const data: ChartDataPoint[] = [];

    // Add a starting point (day before first entry)
    const dayBefore = new Date(earliestDate);
    dayBefore.setDate(dayBefore.getDate() - 1);
    const dayBeforeStr = formatDateISO(dayBefore);

    data.push({
      date: dayBeforeStr,
      total: 0,
      formattedDate: formatDateDisplay(dayBefore),
      isActualDataPoint: false,
    });

    // Create data point for each date
    allDates.forEach((date) => {
      const dateStr = formatDateISO(date);
      const amount = savingsMap.get(dateStr) || 0;
      runningTotal += amount;

      data.push({
        date: dateStr,
        total: runningTotal,
        formattedDate: formatDateDisplay(date),
        isActualDataPoint: savingsMap.has(dateStr),
      });
    });

    setChartData(data);
  }, []);

  useEffect(() => {
    const calculateSavings = async () => {
      setLoading(true);
      try {
        // Fetch user's interest rate and account creation date
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("interest_rate, created_at")
          .single();

        if (userError) {
          console.error("Error fetching user data:", userError);
          setLoading(false);
          return;
        }

        const userInterestRate = userData.interest_rate || 8; // Default to 8% if not set
        setInterestRate(userInterestRate);

        // Calculate 20-year growth factor for $1
        const dollarGrowthFactor = Math.pow(1 + userInterestRate / 100, 20);
        setTwentyYearGrowth(dollarGrowthFactor);
        const today = new Date();

        // Fetch orders
        const { data: ordersData, error: ordersError } = await supabase
          .from("view_reimburse_amount")
          .select("id, purchase_date, reimbursable_amount")
          .order("purchase_date", { ascending: true });

        if (ordersError) {
          console.error("Error fetching orders:", ordersError);
          setLoading(false);
          return;
        }

        // Calculate total available funds (sum of all reimbursable amounts)
        const totalAvailableFunds = ordersData
          ? ordersData.reduce(
            (sum, order) => sum + (order.reimbursable_amount || 0),
            0
          )
          : 0;
        setAvailableFunds(totalAvailableFunds);

        // Set total receipts count
        setTotalReceipts(ordersData ? ordersData.length : 0);
        setHasNoReceipts(ordersData?.length === 0);

        // Set a random example receipt if available
        if (ordersData && ordersData.length > 0) {
          const randomIndex = Math.floor(Math.random() * ordersData.length);
          setExampleReceipt(ordersData[randomIndex]);
        }

        // Calculate total savings with compound interest for today
        const totalSavingsToday = calculateCompoundGrowth(
          ordersData,
          today,
          userInterestRate
        );

        // Calculate total savings for tomorrow to get daily rate
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const totalSavingsTomorrow = calculateCompoundGrowth(
          ordersData,
          tomorrow,
          userInterestRate
        );

        // Calculate daily savings rate
        const dailySavingsRate = totalSavingsTomorrow - totalSavingsToday;

        setTotalAmount(totalSavingsToday);
        setDailySavings(dailySavingsRate);
      } catch (error) {
        console.error("Error calculating savings:", error);
      } finally {
        setLoading(false);
      }
    };

    calculateSavings();

    // Fetch chart data
    const fetchChartData = async () => {
      setChartLoading(true);

      // Fetch chart data from the view
      const { data: chartViewData, error: chartError } = await supabase
        .from("view_savings_chart_data")
        .select("purchase_date, amount");

      if (chartError) {
        console.error("Error fetching chart data:", chartError);
      } else {
        prepareChartData(chartViewData || []);
      }

      setChartLoading(false);
    };

    fetchChartData();
  }, [prepareChartData]);

  // Calculate example receipt growth
  const calculateExampleGrowth = () => {
    if (!exampleReceipt) return { years: 0, growth: 0 };

    const today = new Date();
    const purchaseDate = new Date(exampleReceipt.purchase_date);
    const yearDiff =
      (today.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365);

    const growth =
      exampleReceipt.reimbursable_amount *
      Math.pow(1 + interestRate / 100, yearDiff) -
      exampleReceipt.reimbursable_amount;

    return { years: yearDiff, growth };
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="savings-tooltip">
          <p className="savings-tooltip-date">
            {payload[0].payload.formattedDate}
          </p>
          <p className="savings-tooltip-value">
            ${payload[0].value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </p>
          <p className="savings-tooltip-label">Funds Available</p>
        </div>
      );
    }
    return null;
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Save the files where the upload page picks them up and starts reading them
  const handleFirstReceiptFiles = async (files: FileList | null) => {
    // The upload area clears its file list as soon as this returns, so copy it first
    const selectedFiles = Array.from(files ?? []);
    if (selectedFiles.length === 0) return;

    setUploadError(null);
    try {
      await Promise.all(
        selectedFiles.map((file) =>
          saveFileToStorage(crypto.randomUUID(), file, null, false)
        )
      );
      navigate("/receipt-upload");
    } catch (error) {
      console.error("Error saving uploaded files:", error);
      setUploadError("We couldn't open that file. Please try again.");
    }
  };

  const { years, growth } = calculateExampleGrowth();

  const pageHeader = (
    <div className="page-header">
      <div className="page-header-title">
        <h1>Dashboard</h1>
        <p>Track receipts and view lifetime and realized savings.</p>
      </div>
    </div>
  );

  // Free trial without a card
  const trialBanner = trialEndsAt !== null && (
    <InfoBanner
      icon={<PiClock size={24} />}
      color={daysUntil(trialEndsAt) <= 7 ? "yellow" : "blue"}
      ctaText="Subscribe"
      onCtaClick={() => {
        window.location.href = getCheckoutUrl(userEmail);
      }}
    >
      <p>
        Your free trial ends in{" "}
        <strong>
          {daysUntil(trialEndsAt)}{" "}
          {daysUntil(trialEndsAt) === 1 ? "day" : "days"}
        </strong>
        . Subscribe for $60/year to keep uploading and editing receipts.
      </p>
    </InfoBanner>
  );

  if (hasNoReceipts && draftCount === 0 && pendingUploadCount === 0) {
    return (
      <div className="global-page-screen">
        <div className="page">
          {pageHeader}

          {/* Adding a first receipt comes before subscribing, until the trial is nearly over */}
          {trialEndsAt !== null && daysUntil(trialEndsAt) <= 7 && trialBanner}

          <FirstReceipt
            userEmail={userEmail}
            uploadError={uploadError}
            onFilesSelected={handleFirstReceiptFiles}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="global-page-screen">
      <div className="page">
        {pageHeader}

        {!loading && trialBanner}

        {/* Email uploaded receipts waiting for review */}
        {draftCount > 0 && (
          <InfoBanner
            icon={<PiWarning size={24} />}
            color="yellow"
            ctaText={"Review Receipt" + (draftCount === 1 ? "" : "s")}
            onCtaClick={() => viewDraftReceipts(navigate)}
          >
            <p>
              You have <strong>{draftCount} email uploaded</strong>
              {draftCount !== 1 ? " receipts" : " receipt"} that{" "}
              {draftCount === 1 ? "needs" : "need"} detail confirmation.
            </p>
          </InfoBanner>
        )}

        {/* Uploaded receipts that haven't been added to the library yet */}
        {pendingUploadCount > 0 && (
          <InfoBanner
            icon={<PiUploadSimple size={24} />}
            color="blue"
            ctaText="Finish Uploading"
            onCtaClick={() => navigate("/receipt-upload")}
          >
            <p>
              You have <strong>{pendingUploadCount} uploaded</strong>
              {pendingUploadCount !== 1 ? " receipts" : " receipt"} that{" "}
              {pendingUploadCount === 1 ? "hasn't" : "haven't"} been added to
              your library yet.
            </p>
          </InfoBanner>
        )}

        <div className="savings-summary">
          <div className="savings-summary-content">
            <div className="savings-total">
              <h2 className="container-label-header">
                Total Savings with Shoebox
              </h2>
              {loading ? (
                <p>Calculating your savings...</p>
              ) : (
                <h1>
                  $
                  {new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(totalAmount)}
                </h1>
              )}
              <p>
                The total estimated amount your account has grown from delayed
                reimbursements!
              </p>
            </div>
            <div className="savings-info">
              <p>
                When you wait to get paid back for expenses, you keep more money
                in your account. This extra money can grow through investments,
                giving you funds you wouldn't have had if you paid everything
                upfront. It's like giving yourself a bonus!
              </p>
            </div>
          </div>
          <div className="savings-summary-expand">
            <button
              className="calculation-toggle"
              onClick={toggleExpand}
              aria-expanded={isExpanded}
            >
              <span>How is this calculated?</span>
              {isExpanded ? <PiCaretUp /> : <PiCaretDown />}
            </button>

            {isExpanded && (
              <div className="calculation-details">
                <div className="calculation-method">
                  <h3>Calculation Method</h3>
                  <p>
                    We calculate your savings using compound interest over time.
                    The formula used is:
                  </p>
                  <div className="calculation-formula">
                    <code>Growth = P × (1 + r)^t - P</code>
                    <p>Where:</p>
                    <ul>
                      <li>
                        <strong>P</strong> = Reimbursable amount
                      </li>
                      <li>
                        <strong>r</strong> = Interest rate ({interestRate}%)
                        <button
                          className="text-link"
                          onClick={() => navigate("/settings")}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-blue-600)",
                            padding: "0 0.3rem",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "inherit",
                          }}
                        >
                          (change)
                        </button>
                      </li>
                      <li>
                        <strong>t</strong> = Time in years since purchase
                      </li>
                    </ul>
                  </div>
                </div>

                {exampleReceipt && (
                  <div className="calculation-example">
                    <h3>Example from Your Receipts</h3>
                    <div className="example-receipt">
                      <div className="example-receipt-detail">
                        <span>Purchase Date:</span>
                        <strong>
                          {new Date(
                            exampleReceipt.purchase_date
                          ).toLocaleDateString()}
                        </strong>
                      </div>
                      <div className="example-receipt-detail">
                        <span>Amount:</span>
                        <strong>
                          ${exampleReceipt.reimbursable_amount.toFixed(2)}
                        </strong>
                      </div>
                      <div className="example-receipt-detail">
                        <span>Time Period:</span>
                        <strong>{years.toFixed(2)} years</strong>
                      </div>
                      <div className="example-receipt-detail highlight">
                        <span>Growth:</span>
                        <strong>${growth.toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <FourTileMetrics
          metrics={[
            {
              title: "Savings Per Day",
              value: `$${dailySavings.toFixed(2)}`,
              text: "How much additional you save daily by delaying reimbursements.",
              icon: <PiCaretUpFill />,
              color: "var(--color-green-600)",
            },
            {
              title: "20-Year Growth",
              value: loading
                ? "Calculating..."
                : `$${twentyYearGrowth.toFixed(2)}`,
              text: "What $1 saved today could be worth in 20 years.",
            },
            {
              title: "Total Receipts",
              value: loading ? "Calculating..." : totalReceipts.toString(),
              text: "Total number of receipts submitted.",
            },
            {
              title: "Available Funds",
              value: loading
                ? "Calculating..."
                : `$${new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(availableFunds)}`,
              text: "Available for tax-free withdrawal anytime.",
              color: "var(--color-green-600)",
            },
          ]}
        />

        <div className="savings-graph">
          {chartLoading ? (
            <div className="savings-loading">Loading chart data...</div>
          ) : chartData.length > 0 ? (
            <div className="savings-chart-container">
              <h2 className="container-label-header">
                Your Available Fund Growth Over Time
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-grey-200)"
                  />
                  <XAxis
                    dataKey="formattedDate"
                    tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                    tickMargin={10}
                    axisLine={{ stroke: "var(--color-grey-300)" }}
                    interval="preserveStartEnd"
                    minTickGap={30}
                  />
                  <YAxis
                    tickFormatter={(value) =>
                      `$${value
                        .toFixed(0)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                    }
                    tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                    axisLine={{ stroke: "var(--color-grey-300)" }}
                    tickMargin={10}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="var(--color-grey-400)" />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="var(--color-blue-500)"
                    fill="var(--color-blue-50)"
                    strokeWidth={2}
                    activeDot={{
                      r: 8,
                      fill: "var(--color-blue-700)",
                      className: "savings-chart-dot",
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="container-empty">
              <p>
                You haven't recorded any eligible expenses yet. Start adding
                receipts to build your available fund.
              </p>
              <ActionButton
                onClick={() => navigate("/receipt-upload")}
                text="Add Receipt"
                icon={<PiPlus />}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
